<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="600px" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Neue Aktivität</span>
        <v-spacer></v-spacer>
      </v-card-title>

      <v-card-text>
        <v-form ref="conversationForm">
          <v-select
            v-model="formData.conversation_type"
            :items="conversationTypes"
            label="Aktivität Typ*"
            required
            :error-messages="conversationTypeError"
            @blur="validateForm"
          ></v-select>
          <div class="contact-person-section">
            <v-select
              v-model="formData.contact_person_id"
              :items="contactPersonOptions"
              item-text="name"
              item-value="id"
              label="Kontaktperson zuweisen (optional)"
              clearable
              prepend-icon="mdi-account"
              :append-outer-icon="showQuickAddContact ? 'mdi-chevron-up' : 'mdi-plus'"
              @click:append-outer="toggleQuickAddContact"
            ></v-select>
          </div>

          <!-- Quick Add Contact Person Section-->
          <v-expand-transition>
            <div v-if="showQuickAddContact" class="quick-contact-form">
              <v-card outlined class="mb-4">
                <v-card-text>
                  <div class="d-flex justify-space-between align-center mb-3">
                    <h4 class="text-h6">Neue Kontaktperson</h4>
                    <v-btn icon small @click="closeQuickAddContact">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  
                  <v-text-field
                    v-model="newContactPerson.name"
                    label="Name*"
                    required
                    dense
                    outlined
                    :error-messages="contactNameError"
                    @blur="validateContactForm"
                    class="mb-2"
                  ></v-text-field>


                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="newContactPerson.email"
                        label="Email"
                        type="email"
                        dense
                        outlined
                        :error-messages="contactEmailError"
                        class="mb-2"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="newContactPerson.phone"
                        label="Telefon"
                        dense
                        outlined
                        @input="formatPhoneInput"
                        class="mb-2"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <div class="d-flex justify-end">
                    <v-btn text small @click="closeQuickAddContact" class="mr-2">Abbrechen</v-btn>
                    <v-btn 
                      color="primary" 
                      small 
                      @click="saveContactPerson"
                      :disabled="!isContactFormValid"
                    >
                      speichern
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-expand-transition>

          <v-row>
            <v-col cols="12" md="6">
              <v-menu
                v-model="callbackDateMenu"
                :close-on-content-click="true"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedCallbackDate"
                    label="Rückrufdatum"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="clearCallbackDate"
                    placeholder="Select date"
                    hint=""
                    persistent-hint
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="formData.callback_date"
                  no-title
                  scrollable
                  :min="minDate"
                  @input="onDateSelected"
                ></v-date-picker>
              </v-menu>
            </v-col>
            
          <v-col cols="12" md="6">
              <v-menu
                v-model="callbackTimeMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                :disabled="!formData.callback_date"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedCallbackTime"
                    label="Rückrufzeit"
                    prepend-icon="mdi-clock-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="clearCallbackTime"
                    placeholder="Zeit auswählen"
                    :disabled="!formData.callback_date"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="callbackTimeMenu"
                  v-model="formData.callback_time"
                  format="24hr"
                  scrollable
                  @click:minute="callbackTimeMenu = false"
                ></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Notes -->
          <v-textarea
            v-model="formData.note"
            label="Notizen*"
            rows="5"
            required
            :error-messages="noteError"
            @blur="validateForm"
            placeholder="Geben Sie hier Ihre Notizen ein..."
          ></v-textarea>


          <!-- Recruitment Data Section -->
          <v-expand-transition>
            <div v-if="customer && customer.isCustomer === '0' && formData.conversation_type === 'offer'">
              <v-divider class="my-4"></v-divider>
              
              <h4 class="mb-3">Rekrutierungsdaten</h4>
              
              <v-text-field
                v-model="formData.current_disposal_company"
                label="Aktuelle Entsorgungsfirma"
                outlined
                clearable
                prepend-icon="mdi-truck"
              ></v-text-field>
              
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.current_price"
                    label="Aktueller Preis"
                    type="number"
                    prefix="€"
                    outlined
                    clearable
                    prepend-icon="mdi-currency-eur"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.tires_per_month"
                    label="Reifen pro Monat"
                    type="number"
                    outlined
                    clearable
                    prepend-icon="mdi-car-tire-alert"
                  ></v-text-field>
                </v-col>
              </v-row>
              
              
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-if="formData.conversation_type === 'offer'"
                    v-model="formData.isInterest"
                    :items="conservationStatusOptions"
                    item-text="label"
                    item-value="keyword"
                    label="Gesprächsstatus*"
                    required
                    :error-messages="interestStatusError"
                    class="mb-4"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.label }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item }">
                      {{ item.label }}
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              
              <v-divider class="my-2"></v-divider>
            </div>
          </v-expand-transition> 
          <v-file-input
            v-model="formData.file"
            label="Anlage (optional)"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            prepend-icon="mdi-paperclip"
            show-size
            :clearable="true"
            :loading="uploadingFile"
            @change="onFileSelected"
          ></v-file-input>

          <!-- Upload progress -->
          <v-progress-linear
            v-if="uploadingFile"
            :value="uploadProgress"
            height="10"
            color="primary"
            class="mt-2"
          ></v-progress-linear>

          <v-alert
            v-if="uploadError"
            type="error"
            dense
            class="mt-2"
          >
            {{ uploadError }}
          </v-alert>

          <v-alert
            v-if="resetting"
            type="info"
            dense
            class="mt-2 mb-4"
          >
            Rückruf wird zurückgesetzt...
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close" :disabled="saving || uploadingFile">Abbrechen</v-btn>
        <v-btn 
          color="primary" 
          @click="save" 
          :loading="saving"
          :disabled="!isFormValid || uploadingFile"
        >
          Aktivität speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { storage } from '@/firebase'

export default {
  name: 'ConversationDialog',

  props: {
    value: Boolean,
    customer: Object,
    contactPersons: {
      type: Array,
      default: () => []
    },
    conservationStatuses: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      callbackDateMenu: false,
      callbackTimeMenu: false,
      saving: false,
      uploadingFile: false,
      uploadProgress: 0,
      uploadError: null,
      conversationTypeError: '',
      noteError: '',
      interestStatusError: '',
      resetting: false,

      showQuickAddContact: false,
      contactNameError: '',
      contactEmailError: '',
      
      newContactPerson: {
        name: '',
        email: '',
        phone: ''
      },
      
      formData: {
        conversation_type: null,
        isInterest: 'Unknown',
        note: '',
        file: null,
        contact_person_id: null,
        callback_date: null,
        callback_time: null,
        current_disposal_company: null,
        current_price: null,
        tires_per_month: null
      },

      conversationTypes: [
        { text: 'Anruf', value: 'phone' },
        { text: 'E-Mail', value: 'email' },
        { text: 'Meeting', value: 'meeting' },
        { text: 'Angebot', value: 'offer' },
      ]
    }
  },

  computed: {
    conservationStatusOptions() {
      console.log('Available conservation statuses:', this.conservationStatuses)
      
      if (!this.conservationStatuses || this.conservationStatuses.length === 0) {
        console.log('No conservation statuses available, using fallback')

      }
      
      return this.conservationStatuses.map(status => ({
        label: status.label,
        keyword: status.keyword,
        color: status.color
      }))
    },

    contactPersonOptions() {
      return this.contactPersons.map(contact => ({
        id: contact.id,
        name: `${contact.name}${contact.position ? ` - ${contact.position}` : ''}`
      }))
    },

    formattedCallbackDate() {
      return this.formData.callback_date ? this.formatDateForDisplay(this.formData.callback_date) : ''
    },

    formattedCallbackTime() {
      if (!this.formData.callback_time) return ''

      const timeParts = this.formData.callback_time.split(':')
      const hours = timeParts[0] || '00'
      const minutes = timeParts[1] || '00'
      
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
    },

    formattedCallbackDateTime() {
      if (!this.formData.callback_date) return ''
      
      const date = this.formatDateForDisplay(this.formData.callback_date)
      const time = this.formattedCallbackTime || '09:00'
      
      return `${date} ${time}`
    },

    minDate() {
      return new Date().toISOString().split('T')[0]
    },

    isFormValid() {
      const basicVal= this.formData.conversation_type && 
             this.formData.note && 
             this.formData.note.trim().length > 0
             
      if (this.formData.conversation_type === 'offer') {
        return basicVal && this.formData.isInterest
      }
      return basicVal
    },

    isContactFormValid() {
      return this.newContactPerson.name && 
             this.newContactPerson.name.trim().length > 0 &&
             this.validateEmail()
    },

    hasCallback() {
      return !!this.formData.callback_date
    },

    customerHasCallback() {
      if (!this.customer || !this.customer.callback_date) return false
      const callbackDate = this.customer.callback_date
      return callbackDate !== '0000-00-00' && callbackDate.trim() !== ''
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm()
      } else {
        this.callbackDateMenu = false
        this.callbackTimeMenu = false
        this.showQuickAddContact = false
      }
    },

    'formData.callback_date'(newDate) {
      if (newDate && !this.formData.callback_time) {
        this.formData.callback_time = '09:00:00'
      } else if (!newDate) {
        this.formData.callback_time = null
      }
      if (this.formData.conversation_type === 'offer') {
        this.autoUpdateStatus()
      }
    },

    'formData.conversation_type'(newType, oldType) {
      if (newType !== 'offer' && oldType === 'offer') {
        this.formData.current_disposal_company = null
        this.formData.current_price = null
        this.formData.tires_per_month = null
        this.formData.isInterest = 'Unknown'
      }
    }
  },

  methods: {
  autoUpdateStatus() {

      if (this.formData.conversation_type !== 'offer') {
        return
      }

      if (this.formData.callback_date) {
        const laterStatus = this.conservationStatusOptions.find(status => 
          status.keyword === 'Later'
        )
        if (laterStatus) {
          this.formData.isInterest = laterStatus.keyword
        } else {
          this.formData.isInterest = 'Later'
        }
      } else if (this.formData.isInterest === 'Later') {
        const unknownStatus = this.conservationStatusOptions.find(status =>
          status.keyword === 'Unknown'
        )
        if (unknownStatus) {
          this.formData.isInterest = unknownStatus.keyword
        } else {
          this.formData.isInterest = 'Unknown'
        }
      }
    },

    toggleQuickAddContact() {
      this.showQuickAddContact = !this.showQuickAddContact
      if (this.showQuickAddContact) {
        this.newContactPerson = {
          name: '',
          email: '',
          phone: ''
        }
        this.contactNameError = ''
        this.contactEmailError = ''
      }
    },

    closeQuickAddContact() {
      this.showQuickAddContact = false
    },

    validateContactForm() {
      let isValid = true
      this.contactNameError = ''
      this.contactEmailError = ''

      if (!this.newContactPerson.name || this.newContactPerson.name.trim().length === 0) {
        this.contactNameError = 'Name ist erforderlich'
        isValid = false
      }

      if (this.newContactPerson.email && !this.validateEmail()) {
        this.contactEmailError = 'Ungültige E-Mail-Adresse'
        isValid = false
      }

      return isValid
    },

    validateEmail() {
      if (!this.newContactPerson.email) return true 
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(this.newContactPerson.email)
    },

    formatPhoneInput() {
      if (this.newContactPerson.phone) {
        this.newContactPerson.phone = this.newContactPerson.phone.replace(/[^\d+]/g, '')
      }
    },

    async saveContactPerson() {
      if (!this.validateContactForm()) {
        return
      }

      try {
        const contactData = {
          ...this.newContactPerson,
          customer_id: this.customer.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }

        this.$emit('save-contact-person', contactData)

        this.newContactPerson = {
          name: '',
          email: '',
          phone: ''
        }
        this.contactNameError = ''
        this.contactEmailError = ''
        this.showQuickAddContact = false
        
        console.log('Contact person saved successfully')
        
      } catch (error) {
        console.error('Error saving contact person:', error)
      }
    },
    onFileSelected(file) {
      if (file) {
        console.log('File selected:', file.name, 'Size:', file.size)
        this.uploadError = null
        this.uploadProgress = 0
      }
    },

    async uploadFileToFirebase(file) {
      if (!file) return null

      console.log('Starting file upload process...')
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      })


      if (!storage) {
        console.error('Firebase storage is not initialized!')
        throw new Error('Firebase Storage ist nicht initialisiert. Bitte überprüfen Sie Ihre Firebase-Konfiguration.')
      }

      this.uploadingFile = true
      this.uploadError = null
      this.uploadProgress = 0

      try {
        const timestamp = Date.now()
        const randomString = Math.random().toString(36).substring(2, 8)
        const fileExtension = file.name.split('.').pop()
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
        const fileName = `crm/conversations/${this.customer.id}/${timestamp}_${randomString}_${sanitizedFileName}`
        
        console.log('Uploading file to path:', fileName)
        
        const fileRef = storage.ref().child(fileName)
        
        console.log('File reference created')
        
        const uploadTask = fileRef.put(file)
        
        return new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              this.uploadProgress = progress
              console.log(`Upload progress: ${progress.toFixed(2)}%`)
            },
            (error) => {
              console.error('Upload error:', error)
              console.error('Error code:', error.code)
              console.error('Error message:', error.message)
              
              let errorMessage = 'Upload fehlgeschlagen: '
              switch (error.code) {
                case 'storage/unauthorized':
                  errorMessage += 'Keine Berechtigung zum Hochladen. Bitte überprüfen Sie die Firebase Storage Rules.'
                  break
                case 'storage/canceled':
                  errorMessage += 'Upload wurde abgebrochen.'
                  break
                case 'storage/unknown':
                  errorMessage += 'Ein unbekannter Fehler ist aufgetreten.'
                  break
                default:
                  errorMessage += error.message
              }
              
              this.uploadError = errorMessage
              this.uploadingFile = false
              reject(error)
            },
            async () => {
              try {
                console.log('Upload completed, getting download URL...')
                const downloadURL = await uploadTask.snapshot.ref.getDownloadURL()
                console.log('File uploaded successfully. Download URL:', downloadURL)
                
                const fileData = {
                  url: downloadURL,
                  name: file.name,
                  path: fileName,
                  size: file.size,
                  type: file.type
                }
                
                console.log('Returning file data:', fileData)
                resolve(fileData)
              } catch (error) {
                console.error('Error getting download URL:', error)
                this.uploadError = 'Fehler beim Abrufen der Download-URL'
                reject(error)
              } finally {
                this.uploadingFile = false
                this.uploadProgress = 0
              }
            }
          )
        })
      } catch (error) {
        console.error('Unexpected error in uploadFileToFirebase:', error)
        this.uploadingFile = false
        this.uploadError = `Upload error: ${error.message}`
        throw error
      }
    },

    resetForm() {
      this.formData = {
        conversation_type: null,
        isInterest: 'Unknown',
        note: '',
        file: null,
        contact_person_id: null,
        callback_date: null,
        callback_time: null,
        current_disposal_company: null,
        current_price: null,
        tires_per_month: null
      }
      this.conversationTypeError = ''
      this.noteError = ''
      this.interestStatusError = ''
      this.uploadError = null
      this.uploadProgress = 0
      this.saving = false
      this.uploadingFile = false
      this.showQuickAddContact = false
    },

    clearCallbackDate() {
      this.formData.callback_date = null
      this.formData.callback_time = null
      this.callbackDateMenu = false
      this.callbackTimeMenu = false
      this.autoUpdateStatus()
    },

    clearCallbackTime() {
      this.formData.callback_time = null
      this.callbackTimeMenu = false
    },

    onDateSelected() {
      this.callbackDateMenu = false
    },

    formatDateForDisplay(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}.${month}.${year}`
    },

    validateForm() {
      let isValid = true
      this.conversationTypeError = ''
      this.noteError = ''
      this.interestStatusError = ''

      if (!this.formData.conversation_type) {
        this.conversationTypeError = 'Aktivität Typ ist erforderlich'
        isValid = false
      }

      if (!this.formData.note || this.formData.note.trim().length === 0) {
        this.noteError = 'Notizen sind erforderlich'
        isValid = false
      }

      if (this.formData.conversation_type === 'offer' && !this.formData.isInterest) {
        this.interestStatusError = 'Gesprächsstatus ist erforderlich für Angebote'
        isValid = false
      }

      return isValid
    },
    close() {
      this.$emit('input', false)
    },

    async save() {
      if (!this.validateForm()) {
        return
      }

      this.saving = true
      this.uploadError = null

      try {
        let fileData = null

        
        if (this.formData.file) {
          console.log('Starting file upload...')
          try {
            fileData = await this.uploadFileToFirebase(this.formData.file)
            console.log('File upload completed successfully:', fileData)
          } catch (uploadError) {
            console.error('File upload failed:', uploadError)
            this.saving = false
            return
          }
        }

        let callbackDateTime = null
        let customerUpdates = {}

        customerUpdates.lastConversation = new Date().toISOString()

        if (this.formData.callback_date !== null) {

          if (this.formData.callback_date) {
            let time = this.formData.callback_time || '09:00:00'

            const timeParts = time.split(':')
            let hours = timeParts[0] || '09'
            let minutes = timeParts[1] || '00'
            let seconds = timeParts[2] || '00'

            hours = hours.padStart(2, '0')
            minutes = minutes.padStart(2, '0')
            seconds = seconds.padStart(2, '0')

            callbackDateTime = `${this.formData.callback_date} ${hours}:${minutes}:${seconds}`
            
            console.log('Formatted callback datetime:', callbackDateTime)

            customerUpdates.callback_date = callbackDateTime
            customerUpdates.callback_time = `${hours}:${minutes}:${seconds}`
          } else {
            customerUpdates.callback_date = null
            customerUpdates.callback_time = null
          }
        }

        if (this.formData.conversation_type === 'offer') {
          customerUpdates.isInterest = this.formData.isInterest
        }


        if (this.customer && this.customer.isCustomer === '0' && this.formData.conversation_type === 'offer') {
          customerUpdates = {
            ...customerUpdates,
            current_disposal_company: this.formData.current_disposal_company,
            current_price: this.formData.current_price,
            tires_per_month: this.formData.tires_per_month
          }
        }

        let note = this.buildConversationNote()
        note = note.replace(/\n/g, '[NEWLINE]')
        
        const conversationData = {
          customer_id: this.customer.id,
          conversation_type: this.formData.conversation_type,
          note: note,
          contact_person_id: this.formData.contact_person_id,
          user_id: localStorage.getItem('user_id'),
          created_by: localStorage.getItem('username'),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          file_name: fileData ? fileData.name : null,
          file_path: fileData ? fileData.path : null,
          callback_date: callbackDateTime 
        }

        console.log('Saving conversation with data:', conversationData)
        console.log('Customer updates:', customerUpdates)     
        
        this.$emit('save', {
          conversationData,
          customerUpdates,
          hasCallback: !!this.formData.callback_date,
        })

        this.close()
        
      } catch (error) {
        console.error('Error in conversation dialog:', error)
        console.error('Error stack:', error.stack)
        this.uploadError = 'Fehler beim Speichern der Aktivität: ' + (error.message || 'Unbekannter Fehler')
      } finally {
        this.saving = false
      }
    },
    buildConversationNote() {
      let note = this.formData.note.trim()      
      if (this.customer && this.customer.isCustomer === '0' && this.formData.conversation_type === 'offer') {
        const recruitmentData = [] 
        if (this.formData.current_disposal_company) {
          recruitmentData.push(`Aktuelle Entsorgungsfirma: ${this.formData.current_disposal_company}`)
        }
        if (this.formData.current_price) {
          recruitmentData.push(`Aktueller Preis: ${this.formData.current_price}€`)
        }
        if (this.formData.tires_per_month) {
          recruitmentData.push(`Reifen pro Monat: ${this.formData.tires_per_month}`)
        } 
        if (recruitmentData.length > 0) {
          note += '\n\n--- Rekrutierungsdaten ---\n' + recruitmentData.join('\n')
        }
      }
      
      return note
   },

    formatCallbackDateForNote(callbackDate) {
      if (!callbackDate) return ''
      try {
        const dateObj = new Date(callbackDate)
        const day = String(dateObj.getDate()).padStart(2, '0')
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const year = dateObj.getFullYear()
        const hours = String(dateObj.getHours()).padStart(2, '0')
        const minutes = String(dateObj.getMinutes()).padStart(2, '0')
        return `${day}.${month}.${year} ${hours}:${minutes}`
      } catch (error) {
        return callbackDate
      }
    },
  }
}
</script>

<style scoped>
.quick-contact-form {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.contact-person-section {
  position: relative;
}

@media (max-width: 960px) {
  .v-picker {
    width: 100% !important;
  }
}
</style>